import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { normalizeListResponse } from '../helpers/companyDocumentList'
import { OrganizationCertificateType } from '../models/Company/types'

const QUERY_ROOT = ['organizationCertificateTypes'] as const

export interface OrgCertificateTypeListParams {
    organization: number | null
    includeInactive?: boolean
}

export interface CreateOrganizationCertificateTypeInput {
    organization: number
    name: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

export interface UpdateOrganizationCertificateTypeInput {
    name?: string
    description?: string | null
    required_company_ids?: number[]
    is_active?: boolean
}

const buildListQueryString = (params: OrgCertificateTypeListParams) => {
    if (!params.organization) return ''
    const searchParams = new URLSearchParams()
    searchParams.append('organization', String(params.organization))
    if (params.includeInactive) {
        searchParams.append('include_inactive', 'true')
    }
    const qs = searchParams.toString()
    return qs ? `?${qs}` : ''
}

export const useOrganizationCertificateTypes = (
    params: OrgCertificateTypeListParams,
) => {
    const queryClient = useQueryClient()
    const queryString = buildListQueryString(params)

    const { data: types, isLoading, error, refetch } = useQuery<
        OrganizationCertificateType[],
        Error
    >({
        queryKey: [...QUERY_ROOT, params.organization, params.includeInactive],
        queryFn: async () => {
            const res = await axiosInstance.get<unknown>(
                `/company/organization_certificate_type/${queryString}`,
            )
            return normalizeListResponse<OrganizationCertificateType>(res.data)
        },
        enabled: !!params.organization,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    })

    const createType = useMutation({
        mutationFn: (body: CreateOrganizationCertificateTypeInput) =>
            axiosInstance.post<OrganizationCertificateType>(
                '/company/organization_certificate_type/',
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Certificate type created')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to create certificate type'
            toast.error(msg)
        },
    })

    const updateType = useMutation({
        mutationFn: ({
            id,
            body,
        }: {
            id: number
            body: UpdateOrganizationCertificateTypeInput
        }) =>
            axiosInstance.patch<OrganizationCertificateType>(
                `/company/organization_certificate_type/${id}/`,
                body,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Certificate type updated')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to update certificate type'
            toast.error(msg)
        },
    })

    const deleteType = useMutation({
        mutationFn: (id: number) =>
            axiosInstance.delete(
                `/company/organization_certificate_type/${id}/`,
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [...QUERY_ROOT] })
            toast.success('Certificate type deleted')
        },
        onError: (error: any) => {
            const msg =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                'Failed to delete certificate type'
            toast.error(msg)
        },
    })

    const sortedTypes = [...(types ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
    )

    return {
        certificateTypes: sortedTypes,
        isLoading,
        error,
        refetch,
        createType,
        updateType,
        deleteType,
        isCreating: createType.isLoading,
        isUpdating: updateType.isLoading,
        isDeleting: deleteType.isLoading,
    }
}
